// Section 22 explainability for the Health tab: a number with its explain() factor list, as a
// disclosure that opens with the keyboard (Enter or Space on the summary) and shows its focus
// ring. Rows are labelled from the text table by modifier source. Props only.
import type { Factor } from '../../sim/api';
import { healthText, illnessFactorName } from '../text/en';

interface Props {
  /** The number as shown. */
  value: string;
  factors: readonly Factor[];
  /** What the number is, for screen readers ("Death risk for Ada"). */
  label: string;
}

function trim(x: number): string {
  return (Math.round(x * 1000) / 1000).toString();
}

function effect(f: Factor): string {
  switch (f.op) {
    case 'base':
      return trim(f.value);
    case 'mul':
      return `x${trim(f.value)}`;
    case 'add':
      return f.value >= 0 ? `+${trim(f.value)}` : trim(f.value);
    case 'set':
      return `= ${trim(f.value)}`;
    case 'min':
      return `at most ${trim(f.value)}`;
    case 'max':
      return `at least ${trim(f.value)}`;
  }
}

function rowLabel(f: Factor): string {
  if (f.op === 'base') return healthText.base;
  const name = illnessFactorName(f.source);
  return f.note === undefined || f.note === '' ? name : `${name} (${f.note})`;
}

export function ExplainNumber({ value, factors, label }: Props) {
  return (
    <details class="health-explain">
      <summary class="num" aria-label={`${label}: ${value}. ${healthText.factorsFor(label)}`}>
        {value}
      </summary>
      <table class="health-explain__factors">
        <caption class="visually-hidden">{healthText.factorsFor(label)}</caption>
        <tbody>
          {factors.map((f, i) => (
            <tr key={i}>
              <th scope="row">{rowLabel(f)}</th>
              <td class="num">{effect(f)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  );
}
